import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiDelete } from "../../../services/api";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";

interface DrugGroup {
  group_id: number;
  group_name: string;
}

interface Drug {
  id: number;
  name: string;
  strength?: string;
}

interface GroupItem {
  id: number;
  name: string;
  strength?: string;
  frequency?: string;
  duration_days?: number;
}

interface DrugGroupMapProps {
  group: DrugGroup | null;
  onClose: () => void;
}

export default function DrugGroupMap({ group, onClose }: DrugGroupMapProps) {
  const { showToast } = useToast();
  const [items, setItems] = useState<GroupItem[]>([]);
  const [drugs, setDrugs] = useState<Drug[]>([]);

  const [drugId, setDrugId] = useState("");
  const [freq, setFreq] = useState("1-0-1");
  const [days, setDays] = useState("3");

  useEffect(() => {
    if (!group?.group_id) return;

    const load = async () => {
      try {
        const [groupItems, drugList] = await Promise.all([
          apiGet(`/api/admin/drug-groups/${group.group_id}/drugs`) as Promise<GroupItem[]>,
          apiGet("/api/admin/drugs") as Promise<Drug[]>
        ]);

        setItems(Array.isArray(groupItems) ? groupItems : []);
        setDrugs(Array.isArray(drugList) ? drugList : []);
      } catch (err) {
        console.error("Failed to load map data", err);
      }
    };

    load();
  }, [group]);

  const add = async () => {
    if (!drugId || !group?.group_id) return;
    try {
      await apiPost(`/api/admin/drug-groups/${group.group_id}/drugs`, {
        drug_id: parseInt(drugId, 10),
        frequency: freq,
        duration_days: parseInt(days, 10) || 3
      });
      showToast({ message: "Drug added to group", type: "success" });
      
      const refreshed = await apiGet(
        `/api/admin/drug-groups/${group.group_id}/drugs`
      ) as GroupItem[];
      setItems(Array.isArray(refreshed) ? refreshed : []);
      
      setDrugId("");
    } catch (e) {
      showToast({ message: "Failed to add drug", type: "error" });
    }
  };

  const remove = async (itemId: number) => {
    if (!group?.group_id || !window.confirm("Remove this drug from group?")) return;

    try {
      await apiDelete(`/api/admin/drug-groups/${group.group_id}/drugs/${itemId}`);
      setItems(items.filter((i) => i.id !== itemId));
      showToast({ message: "Drug removed from group", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to remove item", type: "error" });
    }
  };

  if (!group) return null;

  return (
    <div className="drug-map-overlay" onClick={onClose}>
      <div className="drug-map-card" onClick={e => e.stopPropagation()}>
        <div className="drug-map-header">
          <h3 className="drug-map-title">📦 Configure: {group.group_name}</h3>
          <button className="drug-map-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="drug-map-body">
          <div className="drug-map-form">
            <div className="drug-map-field drug-map-field-wide">
              <label className="drug-map-label">Select Drug</label>
              <select value={drugId} onChange={e => setDrugId(e.target.value)} className="drug-map-select">
                <option value="">-- Choose --</option>
                {drugs.map(d => <option key={d.id} value={d.id}>{d.name} {d.strength ? `(${d.strength})` : ""}</option>)}
              </select>
            </div>
            <div className="drug-map-field">
              <label className="drug-map-label">Freq</label>
              <input value={freq} onChange={e => setFreq(e.target.value)} placeholder="1-0-1" className="drug-map-input" />
            </div>
            <div className="drug-map-field">
              <label className="drug-map-label">Days</label>
              <input type="number" value={days} onChange={e => setDays(e.target.value)} className="drug-map-input" />
            </div>
            <Button variant="primary" onClick={add}>Add</Button>
          </div>

          <div className="drug-map-table-wrap">
            <table className="drug-map-table">
              <thead className="drug-map-thead">
                <tr>
                  <th className="drug-map-th">Drug Name</th>
                  <th className="drug-map-th">Frequency</th>
                  <th className="drug-map-th">Duration</th>
                  <th className="drug-map-th drug-map-th-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="drug-map-empty">
                      No drugs in this group yet.
                    </td>
                  </tr>
                ) : (
                  items.map((i) => (
                    <tr key={i.id} className="drug-map-row">
                      <td className="drug-map-cell">
                        <span className="drug-map-name">{i.name}</span>
                        {i.strength && (
                          <span className="drug-map-strength">
                            ({i.strength})
                          </span>
                        )}
                      </td>
                      <td className="drug-map-cell">
                        <span className="drug-map-pill">
                          {i.frequency || "1-0-1"}
                        </span>
                      </td>
                      <td className="drug-map-cell drug-map-duration">{i.duration_days || 3} Days</td>
                      <td className="drug-map-cell drug-map-action">
                        <Button variant="danger" size="sm" onClick={() => remove(i.id)}>Remove</Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
